// app/components/GoogleTranslate.jsx
import React, { useEffect, useState } from 'react'
import { MantineProvider, Menu, Button } from '@mantine/core'
import { GlobeAltIcon } from '@heroicons/react/24/outline'

const GoogleTranslate = () => {
  const [isClient, setIsClient] = useState(false)
  const [language, setLanguage] = useState('en')

  useEffect(() => {
    setIsClient(true)
  }, [])

  // Function to get language from cookie if running in browser
  const getLanguageFromCookie = () => {
    if (!isClient) return 'en'
    const match = document.cookie.match(/(?:^|;\s*)googtrans=([^;]*)/)
    return match ? match[1].split('/')[2] || 'en' : 'en'
  }

  useEffect(() => {
    if (!isClient) return
    setLanguage(getLanguageFromCookie())
  }, [isClient])

  useEffect(() => {
    if (!isClient) return

    if (!window.googleTranslateElementInit) {
      // Define the googleTranslateElementInit function
      window.googleTranslateElementInit = function () {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: 'en,fr,es,ar,fa',
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
          },
          'google_translate_element'
        )
      }

      // Create a script element and load the Google Translate script
      const gtScript = document.createElement('script')
      gtScript.type = 'text/javascript'
      gtScript.async = true
      gtScript.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
      document.body.appendChild(gtScript)
    }
  }, [isClient])

  useEffect(() => {
    if (!isClient) return

    // Adjust text direction based on language in the cookie
    if (language === 'ar' || language === 'fa') {
      document.body.setAttribute('dir', 'rtl')
    } else {
      document.body.setAttribute('dir', 'ltr')
    }
  }, [language, isClient])

  // Function to set a cookie
  const setCookie = (languageCode) => {
    if (!isClient) return

    // Check if we are in production
    const isProduction = import.meta.env.MODE === 'production'

    if (isProduction) {
      // Set for root domain in production
      document.cookie = `googtrans=${languageCode}; path=/; domain=.talentlift.ca`

      // Set for current subdomain
      document.cookie = `googtrans=${languageCode}; path=/`
      console.log('Cookies set for production: .talentlift.ca and current subdomain')
    } else {
      // Set for current domain only in non-production environments
      document.cookie = `googtrans=${languageCode}; path=/`
      console.log('Cookie set for non-production environment: current domain only')
    }
  }

  const handleLanguageChange = (lang) => {
    if (!isClient) return

    setLanguage(lang) // Update the language state
    const languageCode = lang === 'en' ? '/en/en' : '/en/' + lang
    setCookie(languageCode)
    window.location.reload()
  }

  if (!isClient) {
    return null // Return null during server-side rendering
  }

  return (
    <div className="flex items-center space-x-2">
      <MantineProvider>
        {/* Language Selector */}
        <Menu trigger="click-hover" position="bottom-start">
          <Menu.Target>
            <Button variant="subtle" aria-label="Select Language">
              <GlobeAltIcon className="w-5 h-5 text-white mr-2" />
            </Button>
          </Menu.Target>
          <Menu.Dropdown className="bg-secondary border-0 !w-48">
            {/* Language Options */}
            <Menu.Item
              onClick={() => handleLanguageChange('en')}
              className="text-white bg-secondary hover:text-primary text-btn-line"
            >
              English
            </Menu.Item>
            <Menu.Item
              onClick={() => handleLanguageChange('fr')}
              className="text-white bg-secondary hover:text-primary text-btn-line"
            >
              French
            </Menu.Item>
            <Menu.Item
              onClick={() => handleLanguageChange('es')}
              className="text-white bg-secondary hover:text-primary text-btn-line"
            >
              Spanish
            </Menu.Item>
            <Menu.Item
              onClick={() => handleLanguageChange('fa')}
              className="text-white bg-secondary hover:text-primary text-btn-line"
            >
              Persian
            </Menu.Item>
            <Menu.Item
              onClick={() => handleLanguageChange('ar')}
              className="text-white bg-secondary hover:text-primary text-btn-line"
            >
              Arabic
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </MantineProvider>

      {/* Google Translate Element Container */}
      <div id="google_translate_element" style={{ display: 'none' }}></div>
    </div>
  )
}

export default GoogleTranslate
